import baseApi from '@/services/api';

export interface PaginationParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

export interface Module {
  id: string;
  name: string;
  display_name: string;
  description: string;
  permissions: Permission[];
}

export interface Permission {
  id: string;
  name: string;
}

export interface RolePermission {
  id: string;
  role_id: string;
  module_id: string;
  permission_id: string;
  is_granted: boolean;
  module: Module;
  permission: Permission;
}

export interface Role {
  id: string;
  name: string;
  description: string;
  clinic_id: string;
  is_active: boolean;
  is_deleted: boolean;
  created_at: string;
  updated_at: string;
  rolePermissions?: RolePermission[];
  permissionCount?: number;
  userCount?: number;
}

export interface CreateRoleRequest {
  name: string;
  description: string;
  permissions: {
    [moduleName: string]: string[];
  };
}

export interface UpdateRoleRequest {
  name: string;
  description: string;
  permissions: {
    [moduleName: string]: string[];
  };
}

export interface RolesResponse {
  success: boolean;
  data: {
    roles: Role[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
}

export interface RoleResponse {
  success: boolean;
  data: {
    role: Role;
  };
  message?: string;
}

export interface ModulesResponse {
  success: boolean;
  data: {
    modules: Module[];
  };
}

export interface PermissionsResponse {
  success: boolean;
  data: {
    permissions: Permission[];
  };
}

export interface UsersResponse {
  success: boolean;
  data: {
    users: any[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
}

export const roleService = {
  /**
   * Get all roles for the current clinic
   */
  getClinicRoles: async (clinicId: string, params?: PaginationParams & { status?: 'active' | 'inactive' }): Promise<RolesResponse> => {
    const response = await baseApi.get(`/clinic/${clinicId}/roles`, { params });
    return response.data;
  },

  /**
   * Create a new role
   */
  createClinicRole: async (clinicId: string, data: CreateRoleRequest): Promise<RoleResponse> => {
    const response = await baseApi.post(`/clinic/${clinicId}/roles`, data);
    return response.data;
  },

  /**
   * Get role by ID
   */
  getClinicRoleById: async (clinicId: string, id: string): Promise<RoleResponse> => {
    const response = await baseApi.get(`/clinic/${clinicId}/roles/${id}`);
    return response.data;
  },

  /**
   * Update role
   */
  updateClinicRole: async (clinicId: string, id: string, data: UpdateRoleRequest): Promise<RoleResponse> => {
    const response = await baseApi.put(`/clinic/${clinicId}/roles/${id}`, data);
    return response.data;
  },

  /**
   * Toggle role status (active/inactive)
   */
  toggleClinicRoleStatus: async (clinicId: string, id: string): Promise<RoleResponse> => {
    const response = await baseApi.patch(`/clinic/${clinicId}/roles/${id}/status`);
    return response.data;
  },

  /**
   * Delete role (soft delete)
   */
  deleteClinicRole: async (clinicId: string, id: string): Promise<{ success: boolean; message: string }> => {
    const response = await baseApi.delete(`/clinic/${clinicId}/roles/${id}`);
    return response.data;
  },

  /**
   * Get users assigned to a role
   */
  getClinicRoleUsers: async (clinicId: string, id: string, params?: PaginationParams): Promise<UsersResponse> => {
    const response = await baseApi.get(`/clinic/${clinicId}/roles/${id}/users`, { params });
    return response.data;
  },

  /**
   * Get all available modules with their permissions
   */
  getClinicModules: async (): Promise<ModulesResponse> => {
    const response = await baseApi.get('/roles/modules');
    return response.data;
  },

  /**
   * Get all available permissions
   */
  getClinicPermissions: async (): Promise<PermissionsResponse> => {
    const response = await baseApi.get('/roles/permissions');
    return response.data;
  }
};

